import { BottomSheet, RNHostView } from '@expo/ui';
import * as React from 'react';
import { KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { EventEditorCloseButton } from './event-editor-close-button';

type EventEditorSheetProps = Readonly<{
  children: React.ReactNode;
  /** Label for the confirm button. Defaults to "Done". */
  doneLabel?: string;
  error?: string | null;
  isPresented: boolean;
  onDismiss: () => void;
  scroll?: boolean;
  title: string;
}>;

type EventEditorSurfaceProps = Omit<EventEditorSheetProps, 'isPresented'>;

export function EventEditorSurface({
  children,
  doneLabel = 'Done',
  error,
  onDismiss,
  scroll = true,
  title,
}: EventEditorSurfaceProps) {
  const insets = useSafeAreaInsets();

  const content = (
    <View className="flex-1">
      <View className="min-h-14 flex-row items-center justify-between border-b border-border">
        <Text variant="large" className="min-w-0 flex-1 font-sans font-bold">
          {title}
        </Text>
        <EventEditorCloseButton onPress={onDismiss} title={title} />
      </View>
      {error ? (
        <Text className="py-3 text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}
      {children}
    </View>
  );

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={{ flex: 1, width: '100%' }}
    >
      {scroll ? (
        <ScrollView className="flex-1" keyboardShouldPersistTaps="handled" nestedScrollEnabled>
          {content}
        </ScrollView>
      ) : (
        <View className="flex-1">{content}</View>
      )}
      {/*
        A labelled confirm action so every editor surface has an obvious way to
        approve its changes and go back, not only the small close X. Pinned below
        the scroll area so it stays reachable on short screens.
      */}
      <View
        className="border-t border-border px-4 pt-3"
        style={{ paddingBottom: insets.bottom + 12 }}
      >
        <Button accessibilityLabel={`${doneLabel} ${title}`} onPress={onDismiss}>
          <Text>{doneLabel}</Text>
        </Button>
      </View>
    </KeyboardAvoidingView>
  );
}

export function EventEditorSheet({ isPresented, ...surfaceProps }: EventEditorSheetProps) {
  if (!isPresented) {
    return null;
  }

  return (
    <BottomSheet
      isPresented={isPresented}
      onDismiss={surfaceProps.onDismiss}
      showDragIndicator
      snapPoints={['full']}
    >
      <RNHostView>
        <EventEditorSurface {...surfaceProps} />
      </RNHostView>
    </BottomSheet>
  );
}
